import { Breakpoints } from '@mui/material';
import { createContext, useContext, useRef } from 'react';
import useResizeObserver from 'use-resize-observer';
import { createStore, StoreApi } from 'zustand';
import { useTheme } from '../useTheme';
import { BreakpointsStore, getFallbackBreakpointsStore, setActiveBreakpoint } from './breakpoint-provider-model';

interface ElementBreakpointsValue {
  ref: React.RefCallback<HTMLDivElement>;
  store: StoreApi<BreakpointsStore>;
}

const ElementBreakpointsContext = createContext<ElementBreakpointsValue | undefined>(undefined);

export interface ElementBreakpointProviderProps {
  children?: React.ReactNode;
}

/**
 * Context provider that stores information about the responsive state of the chrono-ui breakpoints with respect to a
 * specific html element (usually the primary page content), so that they can be used across many components without
 * introducing performance issues associated with making a huge number of resize observers.
 *
 * A single instance of this component is automatically provided to consumers via the `ThemeProvider`. This instance of
 * the `ElementBreakpointsProvider` should be configured via `useConfigureElementBreakpoints` to work with the
 * primary content on the page (in most cases, this is handled by the `ProtectedApp` component in `cloud`).
 *
 * If multiple sections of a page try to use the same instance of `ElementBreakpointsProvider`, they will overwrite
 * each other and lead to strange behavior. In cases where you need multiple `ElementBreakpointsProvider`s (e.g. you
 * want responsive handling for a `Drawer` that renders over a page), wrap each additional section of the page with its
 * own `ElementBreakpointsProvider` and configure it using `useConfigureElementBreakpoints`.
 *
 * Although multiple `ElementBreakpointsProvider` instances are supported, keep in mind that they do have overhead
 * associated with them (they depend on resize observers) and are intended to help with sizing large sections of a
 * page (e.g. the primary content in `ProtectedApp`). If you find yourself using more than 2-3 of them on a given
 * page, you are probably not using them for their intended use case and may have performance issues.
 */
export function ElementBreakpointsProvider({ children }: ElementBreakpointProviderProps) {
  const {
    theme: { breakpoints },
  } = useTheme();

  // Putting the store in context using a ref per recommendation from zustand docs.
  // https://zustand.docs.pmnd.rs/guides/initialize-state-with-props#wrapping-the-context-provider
  const storeRef = useRef<StoreApi<BreakpointsStore>>();
  if (!storeRef.current) {
    storeRef.current = initializeBreakpointStore(breakpoints);
  }

  const setActiveBreakpoint = storeRef.current.getState().setActiveBreakpoint;

  const { ref } = useResizeObserver<HTMLDivElement>({
    onResize: ({ width }) => {
      if (typeof width === 'number') {
        const activeBreakpoint = getActiveBreakpointFromWidth(breakpoints, width);
        if (activeBreakpoint) {
          setActiveBreakpoint(activeBreakpoint);
        }
      }
    },
  });

  return (
    <ElementBreakpointsContext.Provider
      value={{
        store: storeRef.current,
        ref,
      }}
    >
      {children}
    </ElementBreakpointsContext.Provider>
  );
}

function getActiveBreakpointFromWidth(breakpoints: Breakpoints, width: number) {
  for (let i = 0; i < breakpoints.keys.length; i++) {
    const breakpointKey = breakpoints.keys[i]!;

    if (i < breakpoints.keys.length - 1) {
      // We are sure that `nextBreakpointKey` is not undefined because of the conditional above, but TS does not
      // seem able to infer this, so adding a `!`.
      const nextBreakpointKey = breakpoints.keys[i + 1]!;

      const startWidth = breakpoints.values[breakpointKey];
      const endWidth = breakpoints.values[nextBreakpointKey];

      if (width >= startWidth && width < endWidth) {
        return breakpointKey;
      }
    } else {
      const startWidth = breakpoints.values[breakpointKey];
      if (width >= startWidth) {
        return breakpointKey;
      }
    }
  }
}

function initializeBreakpointStore(breakpoints: Breakpoints): StoreApi<BreakpointsStore> {
  if (process.env.NODE_ENV === 'test' && process.env.SKIP_ELEMENT_BREAKPOINTS_FALLBACK !== 'true') {
    // In test environments, default to using a simple fallback store. This avoids problems with the
    // use-resize-observer/polyfilled used in jest never calling `onResize` because jsdom doesn't really do "layout."
    // In rare cases where we really want to test the full lifecycle of `ElementBreakpointsStore` in jest, this can
    // be turned off with an env var.
    return getFallbackBreakpointsStore();
  }

  // Initialize the store. At this point, it will not have breakpoint data because the ref must be configured via
  // `useConfigureElementBreakpoints` and then measured to determine the correct breakpoint.
  return createStore<BreakpointsStore>()((set) => {
    return {
      isLoading: true,
      setActiveBreakpoint: (breakpoint) =>
        set(() => {
          return setActiveBreakpoint(breakpoints, breakpoint);
        }),
    };
  });
}

export const useElementBreakpointsContext = () => {
  const context = useContext(ElementBreakpointsContext);

  if (!context) {
    if (process.env.NODE_ENV === 'test') {
      // In test environments, we will return a simple fallback store. This minimizes the need for developers to wrap
      // components they are testing in `ThemeProvider` in use cases where they are not trying to test responsive
      // behavior.
      return {
        store: getFallbackBreakpointsStore(),
        ref: () => {},
      };
    } else {
      // In all other environments (development, production), throw an error if the context provider is missing because
      // the responsive behavior will break without it. The provider automatically comes with `ThemeProvider`, which is
      // used near the very top of our application code, so it should be very rare for this to be triggered.
      throw new Error('Missing ElementBreakpointsProvider');
    }
  }

  return context;
};

export const useElementBreakpointsStore = () => {
  const { store } = useElementBreakpointsContext();

  return store;
};
